// Umsortieren per Ziehen (natives HTML5-Drag&Drop, Griff links) wie bei Close: Kontakte im Lead, Nummern und E-Mails im Kontakt.
// Die oberste Zeile ist die bevorzugte. Ohne Bestätigung; Tastatur-Alternative: „Nach oben“ (Knopf bzw. …-Menü).
import { useState, type DragEvent, type HTMLAttributes } from "react";

/** Schalter bis zum Server-Update (19.10.2026): Umsortieren braucht den neuen Endpunkt und die Korrektur „Abmeldung bleibt“. */
export const ORDNEN_AKTIV = true;

/** Eintrag von `von` nach `nach` schieben (neue Liste; unverändert bei gleichem Platz oder ungültigem Index). */
export function verschoben<T>(liste: readonly T[], von: number, nach: number): T[] {
  if (von === nach || von < 0 || nach < 0 || von >= liste.length || nach >= liste.length) return [...liste];
  const neu = [...liste];
  const [eintrag] = neu.splice(von, 1);
  neu.splice(nach, 0, eintrag!);
  return neu;
}

export interface ZiehZeile {
  /** auf den Zeilen-Container */
  zeile: HTMLAttributes<HTMLElement> & { "data-zieht"?: string; "data-ueber"?: string };
  /** auf den Griff */
  griff: HTMLAttributes<HTMLElement> & { draggable: true };
}

/**
 * `art` trennt die Listen voneinander (Kontakte, Nummern, E-Mails eines Kontakts), damit man nichts in die falsche Liste fallen lässt.
 * `onVerschieben(von, nach)` bekommt die Indizes; der Aufrufer baut die neue Liste und speichert.
 */
export function useZiehen(art: string, onVerschieben: (von: number, nach: number) => void) {
  const [zieht, setZieht] = useState<number | null>(null);
  const [ueber, setUeber] = useState<number | null>(null);
  const mime = `application/x-crm-${art}`.toLowerCase(); // dataTransfer.types liefert kleingeschrieben
  const ende = () => {
    setZieht(null);
    setUeber(null);
  };
  const props = (i: number): ZiehZeile => ({
    zeile: {
      "data-zieht": zieht === i ? "ja" : undefined,
      "data-ueber": zieht !== null && ueber === i && zieht !== i ? (zieht < i ? "unten" : "oben") : undefined,
      onDragOver: (e: DragEvent<HTMLElement>) => {
        if (zieht === null || !e.dataTransfer.types.includes(mime)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (ueber !== i) setUeber(i);
      },
      onDrop: (e: DragEvent<HTMLElement>) => {
        if (zieht === null || !e.dataTransfer.types.includes(mime)) return;
        e.preventDefault();
        e.stopPropagation();
        const von = zieht;
        ende();
        onVerschieben(von, i);
      },
    },
    griff: {
      draggable: true,
      onDragStart: (e: DragEvent<HTMLElement>) => {
        e.stopPropagation();
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData(mime, String(i));
        e.dataTransfer.setData("text/plain", "");
        const zeileEl = e.currentTarget.closest("[data-ziehzeile]");
        if (zeileEl) e.dataTransfer.setDragImage(zeileEl, 12, 12);
        setZieht(i);
      },
      onDragEnd: ende,
    },
  });
  return props;
}

export function Griff() {
  return (
    <svg width="8" height="14" viewBox="0 0 10 14" fill="currentColor" aria-hidden="true">
      {[2, 7, 12].map((y) => (
        <g key={y}>
          <circle cx="2.5" cy={y} r="1.1" />
          <circle cx="7.5" cy={y} r="1.1" />
        </g>
      ))}
    </svg>
  );
}
